import "@mantine/core/styles.css";
import "@mantine/spotlight/styles.css";
import "@mantine/code-highlight/styles.css";
import "@mountayaapp/theme/fonts.css";
import "@mountayaapp/theme/reducedMotion.css";
import "./layout/docsChrome.css";

import { CodeHighlightAdapterProvider } from "@mantine/code-highlight";
import { createRoot, hydrateRoot } from "react-dom/client";

import { DEFAULT_LANGUAGE } from "@mountayaapp/locales";
import { shikiAdapter } from "@mountayaapp/markdoc/renderers";
import { ThemeProvider } from "@mountayaapp/theme";

import { App } from "./App";
import { startAnalytics } from "./analytics/rules/start";
import { buildDocHref } from "./content/rules/href";
import { DocsLocaleProvider } from "./locale/providers/DocsLocaleProvider";
import { resolveLanguage } from "./locale/rules/resolveLanguage";
import { DOCS_COLOR_SCHEME_KEY } from "./theme/rules/colorScheme";

/**
 * Client entry: hydrates the prerendered static HTML. The docs app is a
 * framework-free Vite SPA shell over static SSG output — there is no client
 * router, so pages navigate with full reloads against the prerendered tree, and
 * the active page is resolved from the live pathname. The provider runs in
 * user-controlled "auto" mode, persisting the color-scheme choice to the
 * docs-scoped slot so it survives reloads and follows the system preference by
 * default. The shiki highlighter adapter loads on the client and highlights code
 * blocks that shipped as plain text in the static markup. The active language is
 * resolved from the current URL so hydration matches the language the page was
 * prerendered in.
 *
 * The bare root carries no language segment, so it has no authored page to
 * render; before hydrating, it redirects to the default language home. This
 * mirrors the static redirect the prerender writes to `dist/index.html`, and
 * covers the dev server and any host that serves the SPA shell at `/`.
 *
 * Whether the page is hydrated or rendered outright depends on what is actually in
 * the root. A prerendered page arrives with its markup already there and is hydrated.
 * The bare shell — the dev server, which never prerenders, and any host serving
 * `index.html` for a path the build did not emit — arrives with an empty root, and
 * hydrating nothing against a whole tree is a guaranteed mismatch: React reports the
 * failure and throws the pass away on every single load. The cost is not the wasted
 * pass but the noise, which is indistinguishable from a REAL divergence between the
 * static build and the client — the one thing this entry exists to keep honest.
 */
const rootElement = document.getElementById("root");
if (rootElement) {
  const pathname = window.location.pathname;
  const segments = pathname.split("/").filter(Boolean);

  if (segments.length === 0) {
    window.location.replace(buildDocHref(DEFAULT_LANGUAGE, ""));
  } else {
    const language = resolveLanguage(pathname);

    const tree = (
      <ThemeProvider colorScheme="auto" colorSchemeKey={DOCS_COLOR_SCHEME_KEY}>
        <CodeHighlightAdapterProvider adapter={shikiAdapter}>
          <DocsLocaleProvider language={language}>
            <App pathname={pathname} />
          </DocsLocaleProvider>
        </CodeHighlightAdapterProvider>
      </ThemeProvider>
    );

    // firstElementChild, not hasChildNodes: the splice leaves the prerendered markup as
    // an element, while a shell whose root holds only whitespace has nothing to hydrate
    // against and must render outright.
    if (rootElement.firstElementChild) {
      hydrateRoot(rootElement, tree);
    } else {
      createRoot(rootElement).render(tree);
    }

    // Analytics starts only on the client entry, and only after hydration is
    // scheduled: the page has already painted from static markup by this point,
    // and the SDK is a dynamic import so it lands in its own chunk. Keeping the
    // import here rather than anywhere the tree reaches is what stops the
    // prerender step, which renders this same tree under Node, from pulling a
    // browser-only SDK into the static build. The bare root above redirects
    // before hydrating and has no page to report, so it never gets here.
    startAnalytics();
  }
}
